import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  CheckCircleOutline,
  Close,
  ExpandLess,
  ExpandMore,
  NotificationsOffOutlined,
  OpenInNew,
  TuneOutlined,
} from '@mui/icons-material';

import { IHealthCheckResult } from './runHealthCheck';
import {
  SEVERITY_LABEL,
  SEVERITY_PALETTE,
  severityIcon,
} from './findingAppearance';
import {
  HEALTH_CHECK_SECTIONS,
  HealthCheckSection,
  HealthCheckSeverity,
  IHealthCheckFinding,
  IHealthCheckRuleState,
} from './types';

interface IProps {
  open: boolean;
  result: IHealthCheckResult;
  ruleStates: IHealthCheckRuleState[];
  /**
   * true, wenn der Health Check Teil des Speichervorgangs ist. Nur dann gibt es
   * „Dennoch speichern" — bei einem manuell ausgelösten Check nicht (#1283).
   */
  isPartOfSave: boolean;
  onClose: () => void;
  onSaveAnyway: () => void;
  /** Regel für diesen Vorgang dauerhaft ein-/ausschalten. */
  onToggleRule: (ruleKey: string, isActive: boolean) => void;
  /** Abweichung entfernen, wieder der globalen Vorgabe folgen. */
  onResetRule: (ruleKey: string) => void;
  /** Sprung in den passenden Reiter des Vorgangs. */
  onJumpToTab: (tabIndex: number) => void;
}

const SEVERITY_ORDER: Record<HealthCheckSeverity, number> = {
  error: 0,
  warning: 1,
  info: 2,
};

const sortFindings = (findings: IHealthCheckFinding[]) =>
  [...findings].sort(
    (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]
  );

const warningLabel = (count: number) =>
  count === 1 ? '1 Warnung' : `${count} Warnungen`;

/**
 * Ergebnis des Health Checks (#1283).
 *
 * Zwei Wege, mit einem Fund umzugehen, und sie bedeuten Verschiedenes:
 *
 *  - „Dennoch speichern" winkt einmalig durch — prominente Hauptaktion.
 *  - „Nicht mehr prüfen" schaltet die Regel für diesen Vorgang dauerhaft ab —
 *    bewusst klein und mit ausgeschriebener Folge am einzelnen Fund.
 *
 * Ohne diesen Unterschied schalten Nutzer Regeln dauerhaft ab, um eine einmalige
 * Warnung loszuwerden, und der Health Check ist nach einem halben Jahr leer.
 */
export const HealthCheckDialog: React.FC<IProps> = ({
  open,
  result,
  ruleStates,
  isPartOfSave,
  onClose,
  onSaveAnyway,
  onToggleRule,
  onResetRule,
  onJumpToTab,
}) => {
  const [showRules, setShowRules] = useState(false);
  const [showSkipped, setShowSkipped] = useState(false);
  const [ruleSection, setRuleSection] = useState<HealthCheckSection>(
    HEALTH_CHECK_SECTIONS[0]
  );

  const hasFindings = result.findings.length > 0;

  /**
   * Der Dialog bleibt im Baum gemountet, nur `open` wechselt. Ohne dieses
   * Zuruecksetzen bliebe ein einmal aufgeklappter Konfigurationsbereich bei
   * jedem weiteren Oeffnen offen — er ist aber die Ausnahme, nicht der
   * Normalfall, und soll den Blick auf die Funde nicht verstellen.
   */
  useEffect(() => {
    if (open) {
      setShowRules(false);
      setShowSkipped(false);
    }
  }, [open]);

  /**
   * Ausgeliefert ist jede Pruefung aus; erst ein Administrator schaltet frei.
   * Solange nichts aktiv ist, waere „Keine Auffaelligkeiten" eine Zusage ueber
   * einen Vorgang, den gar nichts angesehen hat. Leere `ruleStates` heissen
   * dagegen „noch nicht geladen" und sind keine Aussage.
   */
  const hasActiveRule = ruleStates.some(state => state.isActive);
  const showNothingActivated = ruleStates.length > 0 && !hasActiveRule;

  /** Nur Bereiche, in denen es ueberhaupt Regeln gibt — in Katalog-Reihenfolge. */
  const sectionsWithRules = useMemo(
    () =>
      HEALTH_CHECK_SECTIONS.filter(section =>
        ruleStates.some(state => state.definition.section === section)
      ),
    [ruleStates]
  );

  /**
   * Der zuletzt gewaehlte Bereich kann verschwinden, wenn sich der Katalog
   * aendert. Dann faellt die Auswahl auf den ersten vorhandenen zurueck.
   */
  const activeRuleSection = sectionsWithRules.includes(ruleSection)
    ? ruleSection
    : sectionsWithRules[0];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      fullWidth
      PaperProps={{
        elevation: 8,
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant='h6' component='span' sx={{ fontWeight: 600 }}>
          Health Check
        </Typography>
        {result.warningCount > 0 && (
          <Chip
            size='small'
            color='warning'
            label={warningLabel(result.warningCount)}
          />
        )}
        {/* Schliessen ueber dasselbe onClose wie die Schaltflaeche unten. */}
        <Tooltip title='Schließen'>
          <IconButton
            size='small'
            onClick={onClose}
            sx={{ ml: 'auto' }}
            aria-label='Schließen'
          >
            <Close fontSize='small' />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      <DialogContent
        sx={{
          /*
            MUI setzt den oberen Innenabstand auf 0, sobald der Inhalt direkt
            auf den Titel folgt (`.MuiDialogTitle-root + &` in DialogContent).
            Dieser Selektor ist spezifischer als eine einzelne sx-Klasse — das
            doppelte `&&` gleicht das aus, sonst klebt die erste
            Gruppenueberschrift an der Kopfzeile.
          */
          '&&': { pt: 2.5 },
          pb: 2.5,
        }}
      >
        {showNothingActivated && (
          <Alert severity='info'>
            <AlertTitle>Keine Prüfung aktiviert</AlertTitle>
            Für diesen Vorgang ist derzeit keine Prüfung eingeschaltet — der
            Health Check kann deshalb nichts melden. Ein Administrator schaltet
            die gewünschten Prüfungen unter Einstellungen → Health Check →
            Vorgang-Check frei. Wenden Sie sich bei Bedarf an Ihren
            Administrator.
          </Alert>
        )}

        {!hasFindings && !showNothingActivated && (
          <Alert severity='success' icon={<CheckCircleOutline />}>
            <AlertTitle>Keine Auffälligkeiten</AlertTitle>
            {result.skipped.length === 0
              ? 'Der Health Check hat für diesen Vorgang keine Hinweise gefunden.'
              : `In den ${result.checkedCount} ausgeführten Prüfungen wurden keine Hinweise gefunden.`}
          </Alert>
        )}

        {/*
          Eine Pruefung, die nicht laufen konnte, ist etwas anderes als eine
          Pruefung ohne Fund. Bliebe das unerwaehnt, sagte der Dialog etwas
          ueber einen Vorgang, den er insoweit gar nicht angesehen hat.
          Bewusst zurueckhaltend und aufklappbar: es ist eine Einschraenkung
          des Ergebnisses, kein Fund und erst recht kein Blocker.
        */}
        {result.skipped.length > 0 && (
          <Box sx={{ mt: hasFindings ? 0 : 2, mb: 1 }}>
            <Stack direction='row' spacing={1} alignItems='center'>
              <Typography variant='caption' color='text.secondary'>
                {result.skipped.length === 1
                  ? `Eine von ${result.checkedCount + result.skipped.length} Prüfungen konnte nicht ausgeführt werden.`
                  : `${result.skipped.length} von ${result.checkedCount + result.skipped.length} Prüfungen konnten nicht ausgeführt werden.`}
              </Typography>
              <Button
                size='small'
                onClick={() => setShowSkipped(current => !current)}
                endIcon={showSkipped ? <ExpandLess /> : <ExpandMore />}
              >
                {showSkipped ? 'Ausblenden' : 'Welche?'}
              </Button>
            </Stack>

            <Collapse in={showSkipped} unmountOnExit>
              <Stack spacing={0.5} sx={{ mt: 1, pl: 1 }}>
                {result.skipped.map(entry => (
                  <Box key={entry.ruleKey}>
                    <Typography variant='body2'>{entry.title}</Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {entry.reason}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Collapse>
          </Box>
        )}

        {result.sections.map(section => (
          <Box key={section.section} sx={{ mb: 3 }}>
            <Stack
              direction='row'
              alignItems='center'
              spacing={1}
              sx={{ mb: 1 }}
            >
              <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
                {section.section}
              </Typography>
              {/* Nur die Zahl — das Wort „Warnungen" stand vorher in jeder
                  Gruppenzeile erneut. Fuer Bildschirmleser bleibt es erhalten. */}
              {section.warningCount > 0 && (
                <Chip
                  size='small'
                  variant='outlined'
                  color='warning'
                  label={section.warningCount}
                  aria-label={warningLabel(section.warningCount)}
                />
              )}
            </Stack>

            <Stack spacing={1}>
              {sortFindings(section.findings).map((finding, index) => {
                const palette = SEVERITY_PALETTE[finding.severity];

                return (
                  /*
                    Statt einer vollflaechig eingefaerbten Alert-Box: schmaler
                    Farbstreifen links und ein nur angedeuteter Hintergrund. Die
                    Stufe bleibt an Streifen und Symbol erkennbar, ohne dass bei
                    mehreren Funden die halbe Seite farbig ist. Farbe kommt aus
                    dem Theme, damit die Kanzlei-Farben weiter greifen.
                  */
                  <Box
                    key={`${finding.ruleKey}-${index}`}
                    sx={theme => ({
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.25,
                      px: 1.5,
                      py: 1.25,
                      borderRadius: 1,
                      borderLeft: 4,
                      borderColor: `${palette}.main`,
                      backgroundColor: alpha(
                        theme.palette[palette].main,
                        theme.palette.mode === 'dark' ? 0.16 : 0.07
                      ),
                    })}
                  >
                    <Box
                      sx={{
                        color: `${palette}.main`,
                        display: 'flex',
                        pt: '2px',
                      }}
                    >
                      {severityIcon(finding.severity)}
                    </Box>

                    <Typography
                      variant='body2'
                      sx={{ flexGrow: 1, minWidth: 0 }}
                    >
                      {finding.message}
                    </Typography>

                    <Stack direction='row' spacing={0.5} sx={{ flexShrink: 0 }}>
                      {finding.tabIndex !== undefined && (
                        <Tooltip title='Im Vorgang öffnen'>
                          <IconButton
                            size='small'
                            onClick={() => {
                              onJumpToTab(finding.tabIndex as number);
                              onClose();
                            }}
                          >
                            <OpenInNew fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title='Diese Prüfung für diesen Vorgang dauerhaft deaktivieren'>
                        <IconButton
                          size='small'
                          onClick={() => onToggleRule(finding.ruleKey, false)}
                        >
                          <NotificationsOffOutlined fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        ))}

        {/*
          Konfiguration, nicht Befund — deshalb als eigene, ruhige Flaeche
          abgesetzt. Der Schalter selbst ist unveraendert derselbe State.
        */}
        <Box
          sx={{
            mt: 3,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1.5,
            backgroundColor: 'action.hover',
            overflow: 'hidden',
          }}
        >
          <Button
            fullWidth
            onClick={() => setShowRules(current => !current)}
            startIcon={showRules ? <ExpandLess /> : <ExpandMore />}
            sx={{
              justifyContent: 'flex-start',
              gap: 0.5,
              px: 2,
              py: 1.25,
              color: 'text.primary',
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            <TuneOutlined fontSize='small' sx={{ mr: 1 }} />
            Prüfungen für diesen Vorgang
          </Button>

          {showRules && (
            <Box sx={{ px: 2, pb: 2 }}>
              <Typography
                variant='caption'
                color='text.secondary'
                component='p'
                sx={{ mb: 1.5, maxWidth: 560 }}
              >
                Hier stehen alle Prüfungen — auch die, die derzeit nicht greifen
                und deshalb oben nicht auftauchen. Eine Änderung gilt nur für
                diesen Vorgang und wird im Log festgehalten.
              </Typography>

              {/*
              Unterreiter je Bereich statt einer durchgehenden Liste: mit rund
              30 Regeln wird die Liste sonst so lang, dass man im Dialog nur
              noch scrollt. Die Zahl am Reiter zeigt, wo dieser Vorgang von der
              Vorgabe abweicht — sonst muesste man jeden Reiter durchsehen, um
              das zu finden.
            */}
              <Tabs
                value={activeRuleSection}
                onChange={(_event, value: HealthCheckSection) =>
                  setRuleSection(value)
                }
                variant='scrollable'
                scrollButtons='auto'
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  mb: 0.5,
                  minHeight: 40,
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    minHeight: 40,
                    fontWeight: 500,
                  },
                }}
              >
                {sectionsWithRules.map(section => {
                  const overrideCount = ruleStates.filter(
                    state =>
                      state.definition.section === section &&
                      state.origin === 'transaction'
                  ).length;

                  return (
                    <Tab
                      key={section}
                      value={section}
                      label={
                        overrideCount > 0
                          ? `${section} (${overrideCount})`
                          : section
                      }
                    />
                  );
                })}
              </Tabs>

              <Stack divider={<Divider />}>
                {ruleStates
                  .filter(
                    state => state.definition.section === activeRuleSection
                  )
                  .map(state => (
                    <Stack
                      key={state.ruleKey}
                      direction='row'
                      alignItems='center'
                      spacing={1.5}
                      sx={{ py: 1.25 }}
                    >
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant='body2'>
                          {state.definition.title}
                        </Typography>
                        {/*
                        „Standardvorgabe" stand unter fast jeder Zeile und sagte
                        nichts: es ist der Normalfall. Die Herkunft erscheint
                        deshalb nur noch, wenn sie von der Vorgabe abweicht —
                        „global deaktiviert", „gilt nicht fuer …", der Name des
                        Bearbeiters. Der Zustand selbst bleibt unveraendert.
                      */}
                        {state.origin !== 'catalog' && (
                          <Typography
                            variant='caption'
                            color='text.secondary'
                            component='p'
                          >
                            {state.originLabel}
                          </Typography>
                        )}
                      </Box>

                      {state.origin === 'transaction' && (
                        <Button
                          size='small'
                          sx={{ flexShrink: 0, textTransform: 'none' }}
                          onClick={() => onResetRule(state.ruleKey)}
                        >
                          Zurücksetzen
                        </Button>
                      )}

                      {/* Die Stufe wirkt nur, solange die Pruefung laeuft. */}
                      {state.isActive && (
                        <Chip
                          size='small'
                          variant='outlined'
                          color={SEVERITY_PALETTE[state.severity]}
                          label={SEVERITY_LABEL[state.severity]}
                          sx={{ flexShrink: 0 }}
                        />
                      )}

                      <Switch
                        size='small'
                        checked={state.isActive}
                        onChange={(_event, checked) =>
                          onToggleRule(state.ruleKey, checked)
                        }
                        inputProps={{
                          'aria-label': `${state.definition.title} aktiv`,
                        }}
                      />
                    </Stack>
                  ))}
              </Stack>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}
      >
        <Button variant='outlined' onClick={onClose}>
          Zurück
        </Button>
        {isPartOfSave && (
          <Button variant='contained' onClick={onSaveAnyway}>
            {result.warningCount > 0 ? 'Dennoch speichern' : 'Speichern'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
